import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/i18nContext';
import Button from '../../components/ui/Button';
import '../../styles/AuditLogsPage.css';

const ACTION_FILTERS = [
  'login', 'login_failed', 'logout', 'register',
  'email_verified', 'password_reset', 'password_reset_requested',
  'account_locked', 'session_revoked', 'all_sessions_revoked',
];

const AuditLogsPage = () => {
  const { api } = useAuth();
  const { t } = useI18n();

  const [logs, setLogs]         = useState([]);
  const [pagination, setPag]    = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  // Filters
  const [search, setSearch]     = useState('');
  const [action, setAction]     = useState('');
  const [page, setPage]         = useState(1);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append('search', search);
      if (action) params.append('action', action);

      const { data } = await api.get(`/admin/audit-logs?${params}`);
      setLogs(data.logs);
      setPag(data.pagination);
    } catch {
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [api, page, search, action, t]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Search + action filter debounce — reset to page 1
  const handleSearch = (v) => { setSearch(v); setPage(1); };
  const handleAction = (v) => { setAction(v); setPage(1); };

  const formatDate = (d) => new Date(d).toLocaleString();

  const formatDetails = (details) => {
    if (!details) return '—';
    try {
      const obj = typeof details === 'string' ? JSON.parse(details) : details;
      return Object.entries(obj)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ') || '—';
    } catch {
      return String(details);
    }
  };

  return (
    <div className="audit-page">
      <div className="audit-header">
        <h1 className="audit-title">📋 {t('auditLog.title')}</h1>
        <span className="audit-count">{pagination.total} total</span>
      </div>

      {/* FILTERS */}
      <div className="audit-filters">
        <input
          type="text"
          placeholder={t('auditLog.search')}
          value={search}
          onChange={e => handleSearch(e.target.value)}
          className="audit-search"
        />
        <select
          value={action}
          onChange={e => handleAction(e.target.value)}
          className="audit-select"
        >
          <option value="">{t('auditLog.allActions')}</option>
          {ACTION_FILTERS.map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      </div>

      {error && <p className="audit-error">❌ {error}</p>}

      {/* TABLE */}
      <div className="audit-table-wrap">
        <table className="audit-table">
          <thead>
            <tr>
              <th>{t('auditLog.time')}</th>
              <th>{t('auditLog.user')}</th>
              <th>{t('auditLog.action')}</th>
              <th>{t('auditLog.ip')}</th>
              <th>{t('auditLog.details')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="audit-empty">{t('common.loading')}</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={5} className="audit-empty">{t('auditLog.noLogs')}</td></tr>
            ) : logs.map(log => (
              <tr key={log.id}>
                <td className="audit-time">{formatDate(log.created_at)}</td>
                <td className="audit-user">
                  {log.user_email
                    ? <><strong>{log.user_email}</strong><br/><small>@{log.user_username}</small></>
                    : <span className="audit-unknown">—</span>}
                </td>
                <td>
                  <span className={`audit-badge audit-badge--${log.action.replace(/_/g, '-')}`}>
                    {log.action}
                  </span>
                </td>
                <td className="audit-ip">{log.ip_address || '—'}</td>
                <td className="audit-details">{formatDetails(log.details)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {pagination.pages > 1 && (
        <div className="audit-pagination">
          <Button variant="ghost" size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}>
            ‹
          </Button>
          <span className="audit-page-info">
            {t('auditLog.page')} {pagination.page} {t('auditLog.of')} {pagination.pages}
          </span>
          <Button variant="ghost" size="sm"
            onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
            disabled={page === pagination.pages}>
            ›
          </Button>
        </div>
      )}
    </div>
  );
};

export default AuditLogsPage;
